import { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  fetchProjectDetails,
  fetchProjectsListSimple,
  getLatestServerDate,
  refreshServerDate,
} from "./ddApi";
import { calcHours, emptyWorklogRow, findProjectForTender, projectIdOf, todayStr, WORKLOG_NOTE_OPTIONS, WORKLOG_NOTE_OTHER, yesterdayStr } from "./worklogHelpers";
import { ClockIcon, CloseIcon, DurationBadge, Field, PresetPills, ProjectSearchInput, Sel, TimeSel, TrashIcon } from "./WorkLogShared";
import "./taskPlanner.css";

const MultiWorkLogModal = ({
  isOpen,
  onClose,
  onSave,
  projects = [],
  defaultDate,
  defaultProjectId,
  defaultTender = null,
  allowMultipleRows = true,
}) => {
  const [date, setDate] = useState(defaultDate || todayStr());
  const [rows, setRows] = useState([emptyWorklogRow()]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [detailCache, setDetailCache] = useState({});
  const [loadingDetail, setLoadingDetail] = useState({});
  const [internalProjects, setInternalProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [serverDate, setServerDate] = useState(null);
  const [dateTampered, setDateTampered] = useState(false);
  const [checkingClock, setCheckingClock] = useState(true);

  const projectList = projects.length ? projects : internalProjects;
  const isAnyDropdownOpen = Object.values(openDropdowns).some(Boolean);

  useEffect(() => {
    if (!isOpen) {
      setLoadingProjects(false);
      return;
    }
    if (projects.length) {
      setLoadingProjects(false);
      return;
    }
    let cancelled = false;
    setLoadingProjects(true);
    fetchProjectsListSimple()
      .then((list) => {
        if (!cancelled) setInternalProjects(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (!cancelled) setInternalProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, projects.length]);

  useEffect(() => {
    if (!isOpen) return;
    const first = emptyWorklogRow();
    const matched = findProjectForTender(defaultTender, projectList);
    const pid = projectIdOf(matched) || defaultProjectId || "";
    if (pid) first.projectId = pid;
    setDate(defaultDate || todayStr());
    setRows([first]);
    setErrors({});
    if (pid) ensureProjectDetail(pid);
  }, [isOpen, defaultDate, defaultProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || !projectList.length) return;
    const matched = findProjectForTender(defaultTender, projectList);
    const pid = projectIdOf(matched) || defaultProjectId || "";
    if (!pid) return;
    setRows((prev) => {
      const row = prev[0];
      if (!row) return prev;
      const exists = projectList.some((p) => String(p.id || p.project_id) === String(row.projectId));
      if (row.projectId && exists) return prev;
      ensureProjectDetail(pid);
      return prev.map((r, idx) =>
        idx === 0
          ? {
            ...r,
            projectId: pid,
            // activityId: r.projectId === pid ? r.activityId : "",
            // subActivityId: r.projectId === pid ? r.subActivityId : "",
            // workType: r.projectId === pid ? r.workType : "",
          }
          : r
      );
    });
  }, [isOpen, projectList, defaultTender, defaultProjectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) return;
    setCheckingClock(true);
    refreshServerDate().then(() => {
      const trueNow = getLatestServerDate();
      if (!trueNow) {
        setServerDate(new Date());
        setDateTampered(false);
        setCheckingClock(false);
        return;
      }
      const driftMs = Math.abs(new Date().getTime() - trueNow.getTime());
      setDateTampered(driftMs > 2 * 60 * 1000);
      setServerDate(trueNow);
      setCheckingClock(false);
    });
  }, [isOpen]);

  const maxSelectableDate = useMemo(() => {
    if (!serverDate) return null;
    const d = serverDate;
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, [serverDate]);

  useEffect(() => {
    if (maxSelectableDate && date && date > maxSelectableDate) setDate(maxSelectableDate);
  }, [maxSelectableDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const ensureProjectDetail = useCallback(async (projectId) => {
    if (!projectId || detailCache[projectId] || loadingDetail[projectId]) return;
    setLoadingDetail((prev) => ({ ...prev, [projectId]: true }));
    try {
      const result = await fetchProjectDetails(projectId);
      setDetailCache((prev) => ({ ...prev, [projectId]: result }));
    } catch {
      // keep cache empty on failure
    } finally {
      setLoadingDetail((prev) => ({ ...prev, [projectId]: false }));
    }
  }, [detailCache, loadingDetail]);

  const updateRow = useCallback((id, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== id) return r;
        const u = { ...r, [field]: value };
        if (field === "projectId") {
          u.activityId = "";
          u.subActivityId = "";
          u.workType = "";
        }
        if (field === "activityId") u.subActivityId = "";
        return u;
      })
    );
    setErrors((prev) => {
      const n = { ...prev };
      delete n[`${id}.${field}`];
      return n;
    });
  }, []);

  const handleProjectChange = useCallback((rowId, pid) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== rowId) return r;
        return {
          ...r,
          projectId: pid,
          activityId: "",
          subActivityId: "",
          workType: "",
        };
      })
    );
    setErrors((prev) => {
      const n = { ...prev };
      delete n[`${rowId}.projectId`];
      return n;
    });
    if (pid) ensureProjectDetail(pid);
  }, [ensureProjectDetail]);

  const applyPreset = useCallback((rowId, start, end) => {
    setRows((prev) => prev.map((r) => (r._id === rowId ? { ...r, startTime: start, endTime: end } : r)));
    setErrors((prev) => {
      const n = { ...prev };
      delete n[`${rowId}.startTime`];
      delete n[`${rowId}.endTime`];
      return n;
    });
  }, []);

  const handleNoteOption = useCallback((rowId, option) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r._id !== rowId) return r;
        if (option === WORKLOG_NOTE_OTHER) return { ...r, noteOption: option, note: "" };
        return { ...r, noteOption: option, note: option };
      })
    );
    setErrors((prev) => {
      const n = { ...prev };
      delete n[`${rowId}.noteOption`];
      delete n[`${rowId}.note`];
      return n;
    });
  }, []);

  const addRow = () => setRows((prev) => [...prev, emptyWorklogRow()]);
  const removeRow = (id) => setRows((prev) => (prev.length > 1 ? prev.filter((r) => r._id !== id) : prev));
  const totalHours = rows.reduce((sum, r) => sum + calcHours(r.startTime, r.endTime), 0);

  const validate = () => {
    const errs = {};
    rows.forEach((r) => {
      if (!r.projectId) errs[`${r._id}.projectId`] = true;
      if (!r.startTime) errs[`${r._id}.startTime`] = true;
      if (!r.endTime) errs[`${r._id}.endTime`] = true;
      if (r.startTime && r.endTime && r.endTime <= r.startTime) errs[`${r._id}.endTime`] = true;
      if (!r.noteOption) errs[`${r._id}.noteOption`] = true;
      if (r.noteOption === WORKLOG_NOTE_OTHER && !String(r.note || "").trim()) errs[`${r._id}.note`] = true;
    });
    const today = todayStr();
    const yesterday = yesterdayStr();
    if (!date) errs.date = "Date is required";
    else if (date > today) errs.date = "Future date is not allowed";
    else if (date < yesterday) errs.date = "Only yesterday or today allowed";
    if (totalHours > 9) errs.totalHours = `Total work hours (${totalHours.toFixed(2)} hrs) cannot exceed 9 hrs`;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = rows.map(({ _id, projectType, description, isSelected, taskId, noteOption, ...rest }) => rest);
      await onSave(date, payload);
      onClose();
    } catch {
      // API helpers already toast; keep the modal open
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      centered
      backdrop="static"
      className="tp-worklog-overlay"
      dialogClassName="tp-worklog-dialog"
      contentClassName="tp-worklog-content"
      backdropClassName="tp-worklog-backdrop"
    >
      <div className="tp-modal-head">
        <div className="tp-modal-title-wrap">
          <span className="tp-modal-icon"><ClockIcon /></span>
          <div>
            <h3>Log Work Hours</h3>
            <p>One row per task · max 9 hours</p>
          </div>
        </div>
        <div className="tp-modal-head-right">
          <label className={`tp-date-field ${errors.date ? "error" : ""}`}>
            <span>Date *</span>
            <input
              type="date"
              value={date}
              min={yesterdayStr()}
              max={maxSelectableDate || todayStr()}
              disabled={dateTampered || checkingClock}
              onChange={(e) => {
                setDate(e.target.value);
                setErrors((prev) => {
                  const n = { ...prev };
                  delete n.date;
                  return n;
                });
              }}
            />
          </label>
          <button type="button" className="tp-close-btn" onClick={onClose} title="Close">
            <CloseIcon />
          </button>
        </div>
      </div>
      {errors.date && <div className="tp-error-banner">{errors.date}</div>}
      {dateTampered && (
        <div className="tp-warn-banner">
          Your device's date/time appears incorrect. Please correct your system date to continue.
        </div>
      )}

      <div className={`tp-modal-body ${isAnyDropdownOpen ? "dropdown-open" : ""}`}>
        {loadingProjects ? (
          <div className="tp-modal-loader">
            <span className="tp-modal-spinner" aria-hidden="true" />
            <p>Loading projects…</p>
          </div>
        ) : (
          <div className="tp-log-list">
            {rows.map((row, idx) => {
              const timeInvalid = row.startTime && row.endTime && row.endTime <= row.startTime;
              const e = (f) => !!errors[`${row._id}.${f}`];
              return (
                <div key={row._id} className={`tp-log-card ${openDropdowns[row._id] ? "is-open" : ""}`}>
                  <span className="tp-row-num">{idx + 1}</span>
                  <div className="tp-log-main">
                    <div className="tp-log-row tp-log-row-top">
                      <Field label="Project" required>
                        <ProjectSearchInput
                          projects={projectList}
                          value={row.projectId}
                          onChange={(pid) => handleProjectChange(row._id, pid)}
                          error={e("projectId")}
                          onToggle={(open) => setOpenDropdowns((prev) => ({ ...prev, [row._id]: open }))}
                        />
                      </Field>
                      {/* <Field label="Activity" required>
                      <Sel
                        value={row.activityId}
                        onChange={(v) => updateRow(row._id, "activityId", v)}
                        placeholder="Select activity"
                        disabled={!row.projectId || isLoadingThis}
                        loading={isLoadingThis}
                        error={e("activityId")}
                      >
                        {activities.map((a) => (
                          <option key={a.id} value={a.id}>{a.activity_name}</option>
                        ))}
                      </Sel>
                    </Field> */}
                      {/* <Field label="Sub-activity" required>
                      <Sel
                        value={row.subActivityId}
                        onChange={(v) => updateRow(row._id, "subActivityId", v)}
                        placeholder="Select sub-activity"
                        disabled={!row.activityId || isLoadingThis}
                        error={e("subActivityId")}
                      >
                        {subActivities.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.sorting_var ? `Stage ${s.sorting_var} – ` : ""}{s.subactivity_name}
                          </option>
                        ))}
                      </Sel>
                    </Field> */}
                      {/* <Field label="Work type" required>
                      <Sel
                        value={row.workType}
                        onChange={(v) => updateRow(row._id, "workType", v)}
                        placeholder={!row.projectId ? "Work type" : isLoadingThis ? "Loading…" : workTypes.length === 0 ? "None defined" : "Select type"}
                        disabled={!row.projectId || isLoadingThis || workTypes.length === 0}
                        loading={isLoadingThis}
                        error={e("workType")}
                      >
                        {workTypes.map((wt) => (
                          <option key={wt.id} value={wt.id}>{wt.name}</option>
                        ))}
                      </Sel>
                    </Field> */}
                    </div>
                    <div className="tp-log-row tp-log-row-bottom">
                      <Field label="Start" required>
                        <TimeSel value={row.startTime} onChange={(v) => updateRow(row._id, "startTime", v)} placeholder="In" error={e("startTime")} />
                      </Field>
                      <Field label="End" required>
                        <TimeSel value={row.endTime} onChange={(v) => updateRow(row._id, "endTime", v)} placeholder="Out" error={e("endTime") || timeInvalid} />
                      </Field>
                      <Field label="Hours">
                        <div className="tp-preset-row">
                          <PresetPills onApply={(s, end) => applyPreset(row._id, s, end)} />
                          <DurationBadge start={row.startTime} end={row.endTime} />
                        </div>
                      </Field>
                      <Field label="Note" required>
                        <div className="tp-note-combo">
                          <Sel
                            value={row.noteOption}
                            onChange={(v) => handleNoteOption(row._id, v)}
                            placeholder="Select note"
                            error={e("noteOption")}
                          >
                            {WORKLOG_NOTE_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </Sel>
                          {row.noteOption === WORKLOG_NOTE_OTHER && (
                            <input
                              type="text"
                              className={`tp-input tp-note-input ${e("note") ? "error" : ""}`}
                              value={row.note}
                              onChange={(ev) => updateRow(row._id, "note", ev.target.value)}
                              placeholder="Write note"
                            />
                          )}
                        </div>
                      </Field>
                    </div>
                  </div>
                  {allowMultipleRows && (
                    <button
                      type="button"
                      className="tp-delete-btn"
                      onClick={() => removeRow(row._id)}
                      title="Remove row"
                      disabled={rows.length === 1}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {allowMultipleRows && !loadingProjects && (
        <div className="tp-add-row-wrap">
          <button type="button" className="tp-add-row" onClick={addRow}>+ Add more log</button>
        </div>
      )}

      {Object.keys(errors).length > 0 && (
        <div className="tp-error-banner">
          {errors.totalHours || "Please fill in all required fields and ensure end time is after start time."}
        </div>
      )}

      <div className="tp-modal-foot">
        <div className={`tp-total ${totalHours > 9 ? "over" : ""}`}>
          <span>Total duration</span>
          <strong>{totalHours > 0 ? `${totalHours.toFixed(2)} hrs` : "0.00 hrs"}</strong>
        </div>
        <div className="tp-foot-actions">
          <button type="button" className="tp-btn tp-btn-secondary" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="tp-btn tp-btn-primary"
            onClick={handleSave}
            disabled={saving || checkingClock || dateTampered || loadingProjects}
          >
            {saving ? "Saving…" : allowMultipleRows ? `Save ${rows.length} log${rows.length !== 1 ? "s" : ""}` : "Save log"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default MultiWorkLogModal;
